import { useStyletron } from '@thanx/uikit/theme'
import React, { useEffect, useRef } from 'react'
import { Icon } from '../Icon'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface SelectTriggerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Placeholder text when nothing is selected */
  placeholder?: string
  /** Whether the dropdown is currently open */
  isOpen?: boolean
  /** Whether the trigger is disabled */
  disabled?: boolean
  /** Whether to show the clear button */
  showClear?: boolean
  /** Called when clear button is clicked */
  onClear?: () => void
  /** Called when the trigger is clicked */
  onToggle?: () => void
  /** Search input value (when open, shows input for filtering) */
  searchValue?: string
  /** Called when search input changes */
  onSearchChange?: (value: string) => void
  /** Whether data is currently loading */
  isLoading?: boolean
  /** Content to display (selected value) */
  children?: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled SelectTrigger component.
 *
 * The clickable trigger area of a Select. Shows current selection and icons.
 * When open with searchValue/onSearchChange props, shows a search input.
 * All visual state determined by props. Spreads ...props for HOC injection.
 */
const SelectTrigger = React.memo(
  ({
    placeholder = 'Select...',
    isOpen = false,
    disabled = false,
    showClear = false,
    onClear,
    onToggle,
    searchValue,
    onSearchChange,
    isLoading = false,
    children,
    style,
    onClick,
    className,
    ...props
  }: SelectTriggerProps) => {
    const [css, theme] = useStyletron()
    const inputRef = useRef<HTMLInputElement>(null)

    // Auto-focus input when dropdown opens
    useEffect(() => {
      if (isOpen && onSearchChange && inputRef.current) {
        inputRef.current.focus()
      }
    }, [isOpen, onSearchChange])

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return
      // Don't toggle if clicking on the input
      if ((e.target as HTMLElement).tagName === 'INPUT') return
      onToggle?.()
      onClick?.(e)
    }

    const handleClearClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      onClear?.()
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange?.(e.target.value)
    }

    const handleInputFocus = () => {
      if (!isOpen) {
        onToggle?.()
      }
    }

    const hasValue = React.Children.count(children) > 0
    const showSearchInput = isOpen && onSearchChange !== undefined

    // Compute styles
    const triggerClassName = css(
      getTriggerStyles(theme, { isOpen, disabled, showSearchInput })
    )
    const contentClassName = css(getContentStyles(theme, { hasValue }))
    const searchContainerClassName = css(getSearchContainerStyles())
    const searchIconClassName = css(getSearchIconStyles(theme))
    const inputClassName = css(getInputStyles(theme))
    const iconsContainerClassName = css(getIconsContainerStyles())
    const iconButtonClassName = css(getIconButtonStyles(theme))
    const spinnerClassName = css(getSpinnerStyles(theme))
    const chevronContainerClassName = css(
      getChevronContainerStyles(theme, { isOpen, disabled })
    )
    const chevronClassName = css(getChevronStyles({ isOpen, disabled }))

    // Render search input when open, otherwise show selected value
    const renderContent = () => {
      if (showSearchInput) {
        return (
          <div className={searchContainerClassName}>
            <span className={searchIconClassName}>
              <Icon name="search" size={16} />
            </span>
            <input
              ref={inputRef}
              type="text"
              value={searchValue ?? ''}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              placeholder={hasValue ? String(children) : placeholder}
              className={inputClassName}
            />
          </div>
        )
      }

      return (
        <div className={contentClassName}>
          {hasValue ? children : placeholder}
        </div>
      )
    }

    return (
      <div
        onClick={handleClick}
        className={`${triggerClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {renderContent()}
        <div className={iconsContainerClassName}>
          {isLoading && (
            <span className={spinnerClassName}>
              <Icon name="spinner" size={14} spin />
            </span>
          )}
          {showClear &&
            hasValue &&
            !disabled &&
            !showSearchInput &&
            !isLoading && (
              <button
                type="button"
                onClick={handleClearClick}
                className={iconButtonClassName}
                aria-label="Clear selection"
              >
                <Icon name="x" size={14} />
              </button>
            )}
          <span className={chevronContainerClassName}>
            <span className={chevronClassName}>
              <Icon name="caret-down" size={16} />
            </span>
          </span>
        </div>
      </div>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

interface TriggerStyleParams {
  isOpen: boolean
  disabled: boolean
  showSearchInput: boolean
}

interface ContentStyleParams {
  hasValue: boolean
}

interface ChevronStyleParams {
  isOpen: boolean
  disabled: boolean
}

const getTriggerStyles = (
  theme: any,
  { isOpen, disabled, showSearchInput }: TriggerStyleParams
) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: showSearchInput ? '4px 12px' : '8px 12px',
  backgroundColor: disabled ? theme.colors.grey10 : theme.colors.white,
  borderWidth: '1px',
  borderStyle: 'solid' as const,
  borderColor:
    isOpen && !disabled ? theme.colors.primary400 : theme.colors.grey30,
  borderRadius: theme.borders.radius200,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '14px',
  lineHeight: '20px',
  height: '48px',
  boxSizing: 'border-box' as const,
  transition: 'border-color 150ms ease',
  outline:
    isOpen && !disabled ? `1px solid ${theme.colors.primary400}` : 'none',
  outlineOffset: '-1px',
  color: disabled ? theme.colors.grey50 : theme.colors.grey90,
})

const getContentStyles = (theme: any, { hasValue }: ContentStyleParams) => ({
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  color: hasValue ? 'inherit' : theme.colors.grey50,
})

const getSearchContainerStyles = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flex: 1,
})

const getSearchIconStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.grey50,
  flexShrink: 0,
})

const getInputStyles = (theme: any) => ({
  flex: 1,
  border: 'none',
  outline: 'none',
  fontSize: '14px',
  lineHeight: '20px',
  padding: '6px 0',
  color: theme.colors.grey70,
  backgroundColor: 'transparent',
  '::placeholder': {
    color: theme.colors.grey50,
  },
})

const getIconsContainerStyles = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  marginLeft: '8px',
})

const getIconButtonStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: theme.colors.grey60,
  borderRadius: '2px',
})

const getSpinnerStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.grey50,
})

const getChevronContainerStyles = (
  theme: any,
  { disabled }: ChevronStyleParams
) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  color: theme.colors.grey60,
})

const getChevronStyles = ({ isOpen }: ChevronStyleParams) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 150ms ease',
  transform: isOpen ? 'rotate(180deg)' : 'none',
})

export default SelectTrigger
